import { defaultAdjustmentState } from '../adjustments';
import { COLOR_CHANNEL_NAMES } from '../core/channels';
import { BLACK, WHITE } from '../core/color';
import { clamp } from '../core/math';
import { isPaintTool, type PaintToolId, type ToolId } from '../core/types';
import { TOOL_CYCLES } from '../tools';
import { startFreeTransform, startTransformSelection } from '../tools/transform';
import {
  actualPixels,
  activeDocument,
  addLayer,
  applyAdjustment,
  clearPixels,
  closeDocument,
  copySelection,
  cropToSelection,
  cutSelection,
  deselect,
  duplicateLayerOrViaCopy,
  fillWithColor,
  fitToScreen,
  flattenImage,
  groupActiveLayer,
  invertSelection,
  layerViaCut,
  mergeDown,
  mergeVisible,
  nudgeActiveLayer,
  pasteClipboard,
  patchLayer,
  redo,
  saveDocumentAsPsd,
  selectAdjacentLayer,
  selectAll,
  selectStackEdge,
  stampVisible,
  toggleClippingMask,
  toggleSelectionEdges,
  undo,
  ungroupActiveLayer,
  zoomStep,
} from './actions';
import { setActiveChannel, toggleQuickMask } from './maskActions';
import { growSelectionByColor, reselect, selectSimilarColors } from './selectionActions';
import { openFilesFromPicker } from './openFiles';
import { useAppStore } from './store';
import { pathToSelection } from './vectorActions';

const BRUSH_SIZE_LIMITS = { min: 1, max: 2000 };

export interface Chord {
  /** Layout-independent key: a lowercase letter, a digit or a named key. */
  key: string;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
}

export type CommandGroup = 'File' | 'Edit' | 'Image' | 'Layer' | 'Select' | 'View' | 'Tools';

export interface Command {
  id: string;
  label: string;
  group: CommandGroup;
  /** Display forms, e.g. `Ctrl+Shift+Z`. The first one is the canonical one. */
  chords: string[];
  run: () => void;
}

/** Rows for the shortcuts dialog that are not plain one-chord commands. */
export interface KeyNote {
  keys: string;
  label: string;
  group: CommandGroup;
}

/** Codes whose `event.key` changes with Shift or the keyboard layout. */
const CODE_KEYS: Record<string, string> = {
  Equal: '=',
  Minus: '-',
  BracketLeft: '[',
  BracketRight: ']',
  Comma: ',',
  Period: '.',
  Slash: '/',
  Backslash: '\\',
  Semicolon: ';',
  Quote: "'",
  Backquote: '`',
};

export function parseChord(text: string): Chord {
  const parts = text
    .split('+')
    .map((part) => part.trim().toLowerCase())
    .filter((part) => part.length > 0);
  const chord: Chord = { key: '', ctrl: false, alt: false, shift: false };
  for (const part of parts) {
    if (part === 'ctrl' || part === 'cmd') chord.ctrl = true;
    else if (part === 'alt') chord.alt = true;
    else if (part === 'shift') chord.shift = true;
    else chord.key = part === 'del' ? 'delete' : part === 'esc' ? 'escape' : part;
  }
  return chord;
}

/** Canonical identity of a chord, usable as a lookup key. */
export function chordId(chord: Chord): string {
  return `${chord.ctrl ? 'ctrl+' : ''}${chord.alt ? 'alt+' : ''}${chord.shift ? 'shift+' : ''}${chord.key}`;
}

/**
 * Phonetic Latin stand-ins for Cyrillic letters (keys as \u escapes so this
 * file stays Latin-only). On a Ukrainian layout the glyph for Latin "t" is
 * physical KeyN — a code-only map turns that chord into New Document.
 */
const CYRILLIC_PHONETIC: Record<string, string> = {
  '\u0430': 'a',
  '\u0431': 'b',
  '\u0432': 'v',
  '\u0433': 'h',
  '\u0491': 'g',
  '\u0434': 'd',
  '\u0435': 'e',
  '\u0451': 'e',
  '\u0454': 'e',
  '\u0436': 'z',
  '\u0437': 'z',
  '\u0438': 'y',
  '\u0456': 'i',
  '\u0457': 'i',
  '\u0439': 'y',
  '\u043a': 'k',
  '\u043b': 'l',
  '\u043c': 'm',
  '\u043d': 'n',
  '\u043e': 'o',
  '\u043f': 'p',
  '\u0440': 'r',
  '\u0441': 'c',
  '\u0442': 't',
  '\u0443': 'u',
  '\u0444': 'f',
  '\u0445': 'h',
  '\u0446': 'c',
  '\u0447': 'c',
  '\u0448': 's',
  '\u0449': 's',
  '\u044e': 'u',
  '\u044f': 'y',
};

const CYRILLIC_LETTER = /^[\u0400-\u04ff]$/;

export function chordFromEvent(event: KeyboardEvent): Chord {
  const letter = /^Key([A-Z])$/.exec(event.code);
  const digit = /^Digit(\d)$/.exec(event.code);
  const key = letter
    ? letter[1].toLowerCase()
    : digit
      ? digit[1]
      : (CODE_KEYS[event.code] ?? event.key.toLowerCase());
  return {
    key: key === ' ' ? 'space' : key,
    ctrl: event.ctrlKey || event.metaKey,
    alt: event.altKey,
    shift: event.shiftKey,
  };
}

/**
 * Candidate chords for a key event. Physical `event.code` first (Photoshop
 * scancode behaviour: Ctrl+KeyE is Merge Down even when the layout types
 * Cyrillic u), then a phonetic Latin stand-in when the typed glyph is Cyrillic
 * (so Ctrl + Cyrillic te on KeyN can still reach Transform).
 */
export function chordsFromEvent(event: KeyboardEvent): Chord[] {
  const physical = chordFromEvent(event);
  const typed = event.key.length === 1 ? event.key.toLowerCase() : '';
  const phonetic = CYRILLIC_LETTER.test(typed) ? CYRILLIC_PHONETIC[typed] : undefined;
  if (!phonetic || phonetic === physical.key) return [physical];
  return [physical, { ...physical, key: phonetic }];
}

/**
 * Resolve the command for a key event. Physical chords win over phonetic ones
 * so Ctrl+E / Ctrl+T keep their Photoshop meaning on a Ukrainian layout; the
 * one exception is Cyrillic te on KeyN, where phonetic Transform must beat
 * physical New Document.
 */
export function commandFromEvent(event: KeyboardEvent): Command | undefined {
  const matched: Command[] = [];
  for (const chord of chordsFromEvent(event)) {
    const command = BY_CHORD.get(chordId(chord));
    if (command && !matched.includes(command)) matched.push(command);
  }
  if (matched.length === 0) return undefined;
  if (matched.length === 1) return matched[0];
  const [physicalCmd, phoneticCmd] = matched;
  if (physicalCmd.id === 'file.new' && phoneticCmd.id === 'edit.transform') {
    return phoneticCmd;
  }
  return physicalCmd;
}

/**
 * True when keystrokes should go to a form field. Numeric chrome inputs
 * (`number-input`) still yield to Ctrl/Cmd/Alt chords so Ctrl+A selects the
 * canvas rather than the Size field.
 */
export function isTypingTarget(target: EventTarget | null, event?: KeyboardEvent): boolean {
  if (!target || typeof HTMLElement === 'undefined') return false;
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
    return true;
  }
  if (target.tagName !== 'INPUT') return false;
  const input = target as HTMLInputElement;
  const numericChrome =
    input.classList.contains('number-input') || input.inputMode === 'decimal';
  if (
    numericChrome &&
    event &&
    (event.ctrlKey || event.metaKey || event.altKey)
  ) {
    return false;
  }
  return true;
}

/** The paint tool the brush shortcuts act on, or null outside the paint family. */
function paintTool(): PaintToolId | null {
  const { tool } = useAppStore.getState();
  return isPaintTool(tool) ? tool : null;
}

/** Shared with the canvas tool context menu (right-click while painting). */
export function adjustBrushSize(delta: number): void {
  const tool = paintTool();
  if (!tool) return;
  const store = useAppStore.getState();
  const current = store.paintOptions[tool].size;
  const scaled = delta > 0 ? current * 1.15 + 1 : current / 1.15 - 1;
  store.patchPaintOptions(tool, {
    size: Math.round(clamp(scaled, BRUSH_SIZE_LIMITS.min, BRUSH_SIZE_LIMITS.max)),
  });
}

export function adjustBrushHardness(delta: number): void {
  const tool = paintTool();
  if (!tool) return;
  const store = useAppStore.getState();
  store.patchPaintOptions(tool, {
    hardness: clamp(store.paintOptions[tool].hardness + delta, 0, 1),
  });
}

/**
 * Photoshop maps digits to the opacity of whatever is being edited: the paint
 * tool when one is active, the layer otherwise. `0` means 100%.
 */
function setOpacityFromDigit(digit: number, shift: boolean): boolean {
  const store = useAppStore.getState();
  const opacity = digit === 0 ? 1 : digit / 10;
  const tool = paintTool();
  if (tool) {
    store.patchPaintOptions(tool, shift ? { flow: opacity } : { opacity });
    return true;
  }
  const doc = activeDocument();
  if (!doc?.activeLayerId) return false;
  patchLayer(doc.activeLayerId, { opacity }, 'Layer Opacity');
  return true;
}

function openFile(): void {
  void openFilesFromPicker();
}

function withActiveLayer(action: (layerId: string) => void): void {
  const layerId = activeDocument()?.activeLayerId;
  if (layerId) action(layerId);
}

/** Remembers the tool last chosen inside each cycle, like the Photoshop toolbar. */
const cycleMemory = new Map<string, ToolId>();

function selectFromCycle(key: string, advance: boolean): boolean {
  const cycle = TOOL_CYCLES.find((entry) => entry.key === key);
  if (!cycle) return false;
  const store = useAppStore.getState();
  const index = cycle.tools.indexOf(store.tool);
  const next =
    index >= 0
      ? cycle.tools[(index + (advance ? 1 : 0)) % cycle.tools.length]
      : (cycleMemory.get(key) ?? cycle.tools[0]);
  cycleMemory.set(key, next);
  store.setTool(next);
  return true;
}

export const COMMANDS: Command[] = [
  {
    id: 'file.new',
    label: 'New document',
    group: 'File',
    chords: ['Ctrl+N'],
    run: () => useAppStore.getState().openDialog({ kind: 'newDocument' }),
  },
  { id: 'file.open', label: 'Open file', group: 'File', chords: ['Ctrl+O'], run: openFile },
  {
    id: 'file.save',
    label: 'Save as PSD',
    group: 'File',
    chords: ['Ctrl+S'],
    run: () => void saveDocumentAsPsd(),
  },
  {
    id: 'file.export',
    label: 'Export as',
    group: 'File',
    chords: ['Ctrl+Shift+S', 'Ctrl+Alt+Shift+S'],
    run: () => useAppStore.getState().openDialog({ kind: 'export' }),
  },
  {
    id: 'file.close',
    label: 'Close document',
    group: 'File',
    chords: ['Ctrl+W'],
    run: () => {
      const doc = activeDocument();
      if (doc) closeDocument(doc.id);
    },
  },

  { id: 'edit.undo', label: 'Undo', group: 'Edit', chords: ['Ctrl+Z'], run: undo },
  {
    id: 'edit.redo',
    label: 'Redo',
    group: 'Edit',
    chords: ['Ctrl+Shift+Z', 'Ctrl+Y'],
    run: redo,
  },
  {
    id: 'edit.transform',
    label: 'Free Transform',
    group: 'Edit',
    chords: ['Ctrl+T'],
    run: startFreeTransform,
  },
  {
    id: 'edit.cut',
    label: 'Cut',
    group: 'Edit',
    chords: ['Ctrl+X'],
    run: cutSelection,
  },
  {
    id: 'edit.copy',
    label: 'Copy',
    group: 'Edit',
    chords: ['Ctrl+C'],
    run: () => copySelection(false),
  },
  {
    id: 'edit.copyMerged',
    label: 'Copy Merged',
    group: 'Edit',
    chords: ['Ctrl+Shift+C'],
    run: () => copySelection(true),
  },
  {
    id: 'edit.paste',
    label: 'Paste',
    group: 'Edit',
    chords: ['Ctrl+V'],
    run: () => void pasteClipboard(false),
  },
  {
    id: 'edit.pasteInPlace',
    label: 'Paste in Place',
    group: 'Edit',
    chords: ['Ctrl+Shift+V'],
    run: () => void pasteClipboard(true),
  },
  {
    id: 'edit.fill',
    label: 'Fill',
    group: 'Edit',
    chords: ['Shift+Backspace', 'Shift+Delete', 'Shift+F5'],
    run: () => useAppStore.getState().openDialog({ kind: 'fill' }),
  },
  {
    id: 'edit.stroke',
    label: 'Stroke',
    group: 'Edit',
    chords: [],
    run: () => useAppStore.getState().openDialog({ kind: 'stroke' }),
  },
  {
    id: 'edit.clear',
    label: 'Clear selected pixels',
    group: 'Edit',
    chords: ['Delete', 'Backspace'],
    run: clearPixels,
  },
  {
    id: 'edit.fillForeground',
    label: 'Fill with foreground color',
    group: 'Edit',
    chords: ['Alt+Delete', 'Alt+Backspace'],
    run: () => fillWithColor(useAppStore.getState().foreground),
  },
  {
    id: 'edit.fillBackground',
    label: 'Fill with background color',
    group: 'Edit',
    chords: ['Ctrl+Delete', 'Ctrl+Backspace'],
    run: () => fillWithColor(useAppStore.getState().background),
  },
  {
    id: 'edit.shortcuts',
    label: 'Keyboard shortcuts',
    group: 'Edit',
    chords: ['Ctrl+Alt+Shift+K'],
    run: () => useAppStore.getState().openDialog({ kind: 'shortcuts' }),
  },

  {
    id: 'image.size',
    label: 'Image Size',
    group: 'Image',
    chords: ['Ctrl+Alt+I'],
    run: () => useAppStore.getState().openDialog({ kind: 'imageSize' }),
  },
  {
    id: 'image.canvasSize',
    label: 'Canvas Size',
    group: 'Image',
    chords: ['Ctrl+Alt+C'],
    run: () => useAppStore.getState().openDialog({ kind: 'canvasSize' }),
  },
  {
    id: 'image.crop',
    label: 'Crop',
    group: 'Image',
    chords: [],
    run: cropToSelection,
  },
  {
    id: 'image.levels',
    label: 'Levels',
    group: 'Image',
    chords: ['Ctrl+L'],
    run: () => useAppStore.getState().openDialog({ kind: 'adjustment', adjustment: 'levels' }),
  },
  {
    id: 'image.hsl',
    label: 'Hue/Saturation',
    group: 'Image',
    chords: ['Ctrl+U'],
    run: () => useAppStore.getState().openDialog({ kind: 'adjustment', adjustment: 'hsl' }),
  },
  {
    id: 'image.invert',
    label: 'Invert',
    group: 'Image',
    chords: ['Ctrl+I'],
    run: () => applyAdjustment(defaultAdjustmentState('invert')),
  },
  {
    id: 'image.desaturate',
    label: 'Desaturate',
    group: 'Image',
    chords: ['Ctrl+Shift+U'],
    run: () => applyAdjustment(defaultAdjustmentState('desaturate')),
  },

  { id: 'layer.new', label: 'New layer', group: 'Layer', chords: ['Ctrl+Shift+N'], run: addLayer },
  {
    id: 'layer.duplicate',
    label: 'Duplicate layer / Layer via Copy',
    group: 'Layer',
    chords: ['Ctrl+J'],
    run: duplicateLayerOrViaCopy,
  },
  {
    id: 'layer.viaCut',
    label: 'Layer via Cut',
    group: 'Layer',
    chords: ['Ctrl+Shift+J'],
    run: layerViaCut,
  },
  {
    id: 'layer.mergeDown',
    label: 'Merge down',
    group: 'Layer',
    chords: ['Ctrl+E'],
    run: () => withActiveLayer(mergeDown),
  },
  {
    id: 'layer.mergeVisible',
    label: 'Merge visible',
    group: 'Layer',
    chords: ['Ctrl+Shift+E'],
    run: mergeVisible,
  },
  {
    id: 'layer.stampVisible',
    label: 'Stamp visible',
    group: 'Layer',
    chords: ['Ctrl+Alt+Shift+E'],
    run: stampVisible,
  },
  {
    id: 'layer.flatten',
    label: 'Flatten image',
    group: 'Layer',
    chords: [],
    run: flattenImage,
  },
  {
    id: 'layer.group',
    label: 'Group layers',
    group: 'Layer',
    chords: ['Ctrl+G'],
    run: groupActiveLayer,
  },
  {
    id: 'layer.ungroup',
    label: 'Ungroup layers',
    group: 'Layer',
    chords: ['Ctrl+Shift+G'],
    run: ungroupActiveLayer,
  },
  {
    id: 'layer.clippingMask',
    label: 'Create or release clipping mask',
    group: 'Layer',
    chords: ['Ctrl+Alt+G'],
    run: () => withActiveLayer(toggleClippingMask),
  },
  {
    id: 'layer.bringForward',
    label: 'Bring forward',
    group: 'Layer',
    chords: ['Ctrl+]'],
    run: () => nudgeActiveLayer('forward'),
  },
  {
    id: 'layer.sendBackward',
    label: 'Send backward',
    group: 'Layer',
    chords: ['Ctrl+['],
    run: () => nudgeActiveLayer('backward'),
  },
  {
    id: 'layer.bringToFront',
    label: 'Bring to front',
    group: 'Layer',
    chords: ['Ctrl+Shift+]'],
    run: () => nudgeActiveLayer('front'),
  },
  {
    id: 'layer.sendToBack',
    label: 'Send to back',
    group: 'Layer',
    chords: ['Ctrl+Shift+['],
    run: () => nudgeActiveLayer('back'),
  },
  {
    id: 'layer.selectUp',
    label: 'Select layer above',
    group: 'Layer',
    chords: ['Alt+]'],
    run: () => selectAdjacentLayer(1),
  },
  {
    id: 'layer.selectDown',
    label: 'Select layer below',
    group: 'Layer',
    chords: ['Alt+['],
    run: () => selectAdjacentLayer(-1),
  },
  {
    id: 'layer.selectTop',
    label: 'Select top layer',
    group: 'Layer',
    chords: ['Alt+.'],
    run: () => selectStackEdge('top'),
  },
  {
    id: 'layer.selectBottom',
    label: 'Select bottom layer',
    group: 'Layer',
    chords: ['Alt+,'],
    run: () => selectStackEdge('bottom'),
  },

  { id: 'select.all', label: 'Select all', group: 'Select', chords: ['Ctrl+A'], run: selectAll },
  { id: 'select.none', label: 'Deselect', group: 'Select', chords: ['Ctrl+D'], run: deselect },
  {
    id: 'select.inverse',
    label: 'Inverse selection',
    group: 'Select',
    chords: ['Ctrl+Shift+I', 'Shift+F7'],
    run: invertSelection,
  },
  {
    id: 'select.reselect',
    label: 'Reselect',
    group: 'Select',
    chords: ['Ctrl+Shift+D'],
    run: reselect,
  },
  {
    id: 'select.feather',
    label: 'Feather selection',
    group: 'Select',
    chords: ['Shift+F6'],
    run: () => useAppStore.getState().openDialog({ kind: 'selectionModify', modify: 'feather' }),
  },
  {
    id: 'select.colorRange',
    label: 'Color Range',
    group: 'Select',
    chords: [],
    run: () => useAppStore.getState().openDialog({ kind: 'colorRange' }),
  },
  {
    id: 'select.grow',
    label: 'Grow selection',
    group: 'Select',
    chords: [],
    run: growSelectionByColor,
  },
  {
    id: 'select.similar',
    label: 'Select similar',
    group: 'Select',
    chords: [],
    run: selectSimilarColors,
  },
  {
    id: 'select.refineEdge',
    label: 'Refine Edge',
    group: 'Select',
    chords: [],
    run: () => useAppStore.getState().openDialog({ kind: 'refineEdge' }),
  },
  {
    id: 'select.transform',
    label: 'Transform Selection',
    group: 'Select',
    chords: [],
    run: startTransformSelection,
  },
  {
    id: 'path.toSelection',
    label: 'Make selection from path',
    group: 'Select',
    chords: ['Ctrl+Enter'],
    run: () => {
      const doc = activeDocument();
      if (doc?.activePathId) pathToSelection(doc.activePathId);
    },
  },

  {
    id: 'view.zoomIn',
    label: 'Zoom in',
    group: 'View',
    chords: ['Ctrl+=', 'Ctrl+Shift+='],
    run: () => zoomStep(1),
  },
  {
    id: 'view.zoomOut',
    label: 'Zoom out',
    group: 'View',
    chords: ['Ctrl+-'],
    run: () => zoomStep(-1),
  },
  { id: 'view.fit', label: 'Fit on screen', group: 'View', chords: ['Ctrl+0'], run: fitToScreen },
  {
    id: 'view.actualPixels',
    label: 'Actual pixels',
    group: 'View',
    chords: ['Ctrl+1'],
    run: actualPixels,
  },
  {
    id: 'view.extras',
    label: 'Show or hide extras (selection edges)',
    group: 'View',
    chords: ['Ctrl+H'],
    run: toggleSelectionEdges,
  },
  {
    id: 'view.rulers',
    label: 'Show or hide rulers',
    group: 'View',
    chords: ['Ctrl+R'],
    run: () => useAppStore.getState().toggleRulers(),
  },
  {
    id: 'view.guides',
    label: 'Show or hide guides',
    group: 'View',
    chords: ['Ctrl+;'],
    run: () => useAppStore.getState().toggleGuides(),
  },
  {
    id: 'view.grid',
    label: 'Show or hide the grid',
    group: 'View',
    chords: ["Ctrl+'"],
    run: () => useAppStore.getState().toggleGrid(),
  },
  {
    id: 'view.snap',
    label: 'Snap',
    group: 'View',
    chords: ['Ctrl+Shift+;'],
    run: () => {
      const store = useAppStore.getState();
      store.patchSnap({ enabled: !store.snap.enabled });
    },
  },
  {
    id: 'view.chrome',
    label: 'Hide or show all panels',
    group: 'View',
    chords: ['Tab'],
    run: () => useAppStore.getState().toggleChrome(),
  },
  {
    id: 'view.sidePanels',
    label: 'Hide or show side panels',
    group: 'View',
    chords: ['Shift+Tab'],
    run: () => useAppStore.getState().togglePanelColumn(),
  },
  {
    id: 'view.brushPanel',
    label: 'Brush panel',
    group: 'View',
    chords: ['F5'],
    run: () => useAppStore.getState().togglePanel('brush'),
  },
  {
    id: 'view.colorPanel',
    label: 'Color panel',
    group: 'View',
    chords: ['F6'],
    run: () => useAppStore.getState().togglePanel('color'),
  },
  {
    id: 'view.layersPanel',
    label: 'Layers panel',
    group: 'View',
    chords: ['F7'],
    run: () => useAppStore.getState().togglePanel('layers'),
  },
  {
    id: 'view.infoPanel',
    label: 'Info panel',
    group: 'View',
    chords: ['F8'],
    run: () => useAppStore.getState().togglePanel('info'),
  },
  {
    id: 'view.historyPanel',
    label: 'History panel',
    group: 'View',
    chords: ['F9'],
    run: () => useAppStore.getState().togglePanel('history'),
  },
  {
    id: 'view.channelsPanel',
    label: 'Channels panel',
    group: 'View',
    chords: [],
    run: () => useAppStore.getState().togglePanel('channels'),
  },
  {
    id: 'view.pathsPanel',
    label: 'Paths panel',
    group: 'View',
    chords: ['F10'],
    run: () => useAppStore.getState().togglePanel('paths'),
  },
  {
    id: 'select.composite',
    label: 'View all channels',
    group: 'Select',
    chords: ['Ctrl+2'],
    run: () => setActiveChannel({ kind: 'composite' }),
  },
  ...([0, 1, 2] as const).map((index) => ({
    id: `select.channel${index}`,
    label: `View ${COLOR_CHANNEL_NAMES[index]} channel`,
    group: 'Select' as const,
    chords: [`Ctrl+${3 + index}`],
    run: () => setActiveChannel({ kind: 'color', index }),
  })),

  {
    id: 'tools.swapColors',
    label: 'Swap foreground and background',
    group: 'Tools',
    chords: ['X'],
    run: () => useAppStore.getState().swapColors(),
  },
  {
    id: 'tools.defaultColors',
    label: 'Default colors (black and white)',
    group: 'Tools',
    chords: ['D'],
    run: () => {
      const store = useAppStore.getState();
      store.setForeground(BLACK);
      store.setBackground(WHITE);
    },
  },
  {
    id: 'tools.quickMask',
    label: 'Quick Mask mode',
    group: 'Tools',
    chords: ['Q'],
    run: toggleQuickMask,
  },
  {
    id: 'tools.sizeDown',
    label: 'Decrease brush size',
    group: 'Tools',
    chords: ['['],
    run: () => adjustBrushSize(-1),
  },
  {
    id: 'tools.sizeUp',
    label: 'Increase brush size',
    group: 'Tools',
    chords: [']'],
    run: () => adjustBrushSize(1),
  },
  {
    id: 'tools.hardnessDown',
    label: 'Decrease brush hardness',
    group: 'Tools',
    chords: ['Shift+['],
    run: () => adjustBrushHardness(-0.25),
  },
  {
    id: 'tools.hardnessUp',
    label: 'Increase brush hardness',
    group: 'Tools',
    chords: ['Shift+]'],
    run: () => adjustBrushHardness(0.25),
  },
];

export const KEY_NOTES: KeyNote[] = [
  { keys: 'Escape', label: 'Close a dialog or cancel a gesture', group: 'Edit' },
  { keys: '1 … 0', label: 'Opacity of the paint tool or the active layer', group: 'Tools' },
  { keys: 'Shift+1 … 0', label: 'Flow of the paint tool', group: 'Tools' },
  {
    keys: 'V M L W C B E S G I J R O T P A U H Z',
    label: 'Select a tool',
    group: 'Tools',
  },
  {
    keys: 'Type tool',
    label: 'Click to type; Ctrl+Enter commits the text',
    group: 'Tools',
  },
  { keys: 'Shift+letter', label: 'Cycle the tools that share a letter', group: 'Tools' },
  {
    keys: 'Shift / Alt / Shift+Alt while selecting',
    label: 'Add to / subtract from / intersect selection',
    group: 'Select',
  },
  {
    keys: 'Click (New mode)',
    label: 'Deselect with a marquee or lasso click',
    group: 'Select',
  },
  {
    keys: 'Shift / Alt while transforming',
    label: 'Constrain proportions / transform from centre',
    group: 'Edit',
  },
  { keys: 'Space+drag', label: 'Temporary Hand tool', group: 'View' },
  { keys: 'Alt+wheel', label: 'Zoom', group: 'View' },
  { keys: 'Alt+click swatch', label: 'Set the background colour', group: 'Tools' },
  { keys: 'Alt+drag (paint)', label: 'Sample colour with the eyedropper', group: 'Tools' },
  {
    keys: 'Shift+click (paint)',
    label: 'Straight stroke from the last brush tip',
    group: 'Tools',
  },
  {
    keys: 'Shift+drag (paint)',
    label: 'Constrain the stroke to 0°, 45° or 90°',
    group: 'Tools',
  },
];

const BY_CHORD = new Map<string, Command>();
for (const command of COMMANDS) {
  for (const chord of command.chords) BY_CHORD.set(chordId(parseChord(chord)), command);
}

/** Chords a tool letter or a digit occupies; used by the conflict test. */
export const RESERVED_CHORDS: string[] = [
  ...TOOL_CYCLES.flatMap((cycle) => [cycle.key, `shift+${cycle.key}`]),
  ...'0123456789'.split(''),
];

/** True when the event is an app chord that must not reach the browser (e.g. Ctrl+T). */
export function isAppChord(event: KeyboardEvent): boolean {
  if (isTypingTarget(event.target, event)) return false;
  const chord = chordFromEvent(event);
  if (chord.key === 'escape') return Boolean(useAppStore.getState().dialog);
  return commandFromEvent(event) !== undefined;
}

/**
 * Application-level shortcuts. Called after the active tool has had a chance to
 * consume the event, so a tool can override any of these while it is mid-gesture.
 */
export function handleKeymap(event: KeyboardEvent): boolean {
  if (isTypingTarget(event.target, event)) return false;
  const chord = chordFromEvent(event);
  const store = useAppStore.getState();

  if (chord.key === 'escape') {
    if (!store.dialog) return false;
    store.closeDialog();
    return true;
  }

  const command = commandFromEvent(event);
  if (command) {
    // Block browser defaults (Ctrl+T new tab, Ctrl+N, …) even when a modal is
    // open; the command itself only runs when the UI is free.
    if (store.dialog) return true;
    const target = event.target;
    if (
      typeof HTMLElement !== 'undefined' &&
      target instanceof HTMLElement &&
      target.classList.contains('number-input')
    ) {
      target.blur();
    }
    command.run();
    return true;
  }

  if (chord.ctrl || chord.alt) return false;

  // Tool letters: also honour Cyrillic phonetic keys (te → Type tool's T).
  for (const candidate of chordsFromEvent(event)) {
    if (/^\d$/.test(candidate.key)) return setOpacityFromDigit(Number(candidate.key), candidate.shift);
    if (/^[a-z]$/.test(candidate.key)) return selectFromCycle(candidate.key, candidate.shift);
  }

  return false;
}

/** Chord shown next to a menu item, so menus and the keymap cannot drift apart. */
export function commandChord(id: string): string | undefined {
  return COMMANDS.find((command) => command.id === id)?.chords[0];
}
